import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Shield, Lock, Eye, EyeOff, AlertTriangle } from 'lucide-react';

// Master password for Super Admin bypass
const MASTER_PASSWORD = '050378';

export const GhostProtocolModal = ({ 
  isOpen, 
  onClose, 
  onSuccess, 
  userName = 'User',
  requiredPassword = '',
  title = 'Ghost Protocol Protection',
  description = 'This account is protected. Enter the security password to continue.'
}) => {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [attempts, setAttempts] = useState(0);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Check if password matches either the set password or master password
    if (password === requiredPassword || password === MASTER_PASSWORD) {
      setPassword('');
      setError('');
      setAttempts(0);
      onSuccess();
    } else {
      setAttempts(prev => prev + 1);
      setError('Invalid password. Access denied.');
      setPassword('');
      
      // After 3 failed attempts, show warning
      if (attempts >= 2) {
        setError('Multiple failed attempts detected. Contact your administrator.');
      }
    }
  };

  const handleClose = () => {
    setPassword('');
    setError('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent 
        className="max-w-md"
        style={{
          boxShadow: '0 0 40px rgba(245, 158, 11, 0.4), 0 0 80px rgba(245, 158, 11, 0.2)'
        }}
      >
        <DialogHeader className="text-center pb-2">
          <div className="flex flex-col items-center gap-4">
            <div 
              className="h-20 w-20 rounded-full bg-amber-100 dark:bg-amber-900/50 flex items-center justify-center border-2 border-amber-400"
              style={{
                boxShadow: '0 0 25px rgba(245, 158, 11, 0.4)'
              }}
            >
              <Shield className="h-10 w-10 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <DialogTitle className="text-2xl font-bold text-amber-600 dark:text-amber-400">
                {title}
              </DialogTitle>
              <DialogDescription className="mt-2 text-base">
                {description}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        
        <div className="pt-4">
          <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-3 mb-4">
            <div className="flex items-center gap-2">
              <Lock className="h-4 w-4 text-amber-600" />
              <span className="text-sm font-medium text-amber-800 dark:text-amber-200">
                Accessing: {userName}
              </span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="ghost-password" className="text-amber-800 dark:text-amber-200">
                Security Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-amber-500" />
                <Input
                  id="ghost-password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter security password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setError('');
                  }}
                  className="pl-10 pr-10 border-amber-300 dark:border-amber-700 focus:ring-amber-500"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-amber-500 hover:text-amber-700"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {error && (
                <p className="text-sm text-red-500 flex items-center gap-1 mt-2">
                  <AlertTriangle className="h-3 w-3" />
                  {error}
                </p>
              )}
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button 
                type="button" 
                variant="outline" 
                onClick={handleClose}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
              >
                <Shield className="h-4 w-4 mr-2" />
                Verify Access
              </Button>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default GhostProtocolModal;
